import { admin2 } from './instance'

const clientBaseUrl = (shopId) => `/${shopId}/shopCustomModule/cyjPos`
const levelDiscountBaseUrl = (shopId) => `/${shopId}/cyjPosLevelDiscountConfig`
const branchBaseUrl = (shopId) => `/${shopId}/cyjPos/branch`

/**
 * 手動重試建立 CYJ POS 課程訂金單
 * admin.cyjPosCourse.retryDeposit
 * @param {Object} params - 重試參數
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.memberStoreOrderProductId - 會員商城訂單商品 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 重試結果
 */
export const RetryCyjPosCourseDeposit = async ({ shopId, memberStoreOrderProductId }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/cyjPosCourse/retryDeposit`,
    data: {
      memberStoreOrderProductId,
    },
  })
}

/**
 * @typedef {Object} CustomDisplaySetting
 * @property {string} type - 類型
 * @property {string} [displayName] - 顯示名稱
 * @property {string} [displayUnit] - 顯示單位
 */

/**
 * 建立 CYJ POS 串接設定
 * admin.shopCustomModule.createCyjPosClient
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {boolean} params.enabled - 啟用狀態
 * @param {Object} params.config - 串接設定
 * @param {string} params.config.branchNo - 門市編號（必填）
 * @param {boolean} [params.config.enableManualRedemption=false] - 是否開放顧客端手動核銷
 * @param {boolean} [params.config.enableManualUpdate=false] - 是否啟用後台刷新按鈕
 * @param {Array<CustomDisplaySetting>} [params.config.customDisplaySettings] - 客製顯示設定
 * @returns {import('@/api/instance').Api2Response<Object>} 建立後的 ShopCustomModule 物件
 */
export const CreateCyjPosClient = async ({ shopId, enabled, config }) => {
  return admin2({
    method: 'POST',
    url: clientBaseUrl(shopId),
    data: { enabled, config },
  })
}

/**
 * 更新 CYJ POS 串接設定
 * admin.shopCustomModule.updateCyjPosClient
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {boolean} [params.enabled] - 啟用狀態
 * @param {Object} [params.config] - 串接設定（欄位皆為 optional）
 * @param {string} [params.config.branchNo] - 門市編號
 * @param {boolean} [params.config.enableManualRedemption] - 是否開放顧客端手動核銷
 * @param {boolean} [params.config.enableManualUpdate] - 是否啟用後台刷新按鈕
 * @param {Array<CustomDisplaySetting>} [params.config.customDisplaySettings] - 客製顯示設定
 * @returns {import('@/api/instance').Api2Response<{ msg: string }>} 更新結果
 */
export const UpdateCyjPosClient = async ({ shopId, enabled, config }) => {
  return admin2({
    method: 'PUT',
    url: clientBaseUrl(shopId),
    data: { enabled, config },
  })
}

/**
 * 刪除 CYJ POS 串接設定
 * admin.shopCustomModule.deleteCyjPosClient
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{ msg: string }>} 刪除結果
 */
export const DeleteCyjPosClient = async ({ shopId }) => {
  return admin2({
    method: 'DELETE',
    url: clientBaseUrl(shopId),
  })
}

/**
 * 測試 CYJ POS 連線
 * admin.shopCustomModule.testCyjPosConnection
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{ success: boolean }>} 連線測試結果
 */
export const TestCyjPosConnection = async ({ shopId }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/shopCustomModule/testCyjPosConnection`,
  })
}

/**
 * 手動同步 CYJ POS 票券至 BOB couponCyjPos
 * admin.shopCustomModule.syncCyjPosCoupon
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{ success: boolean, msg: string }>} 同步結果
 */
export const SyncCyjPosCoupon = async ({ shopId }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/shopCustomModule/syncCyjPosCoupon`,
  })
}

/**
 * 手動同步 CYJ POS 消費紀錄
 * admin.shopCustomModule.manualSyncCyjPosTransactions
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{ success: boolean }>} 同步結果
 */
export const ManualSyncCyjPosTransactions = async ({ shopId }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/shopCustomModule/manualSyncCyjPosTransactions`,
  })
}

/**
 * 查詢 CYJ POS 等級折扣設定列表
 * admin.cyjPosLevelDiscountConfig.find
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {number} [params.start=0] - 分頁起始
 * @param {number} [params.limit] - 筆數上限
 * @returns {import('@/api/instance').Api2Response<Array>} CyjPosLevelDiscountConfig 列表
 */
export const GetCyjPosLevelDiscountConfigs = async ({ shopId, start, limit }) => {
  return admin2({
    method: 'GET',
    url: levelDiscountBaseUrl(shopId),
    params: { start, limit },
  })
}

/**
 * 建立 CYJ POS 等級折扣設定
 * admin.cyjPosLevelDiscountConfig.create
 * @param {Object} params - 建立參數
 * @param {string} params.shopId - 店家 ID
 * @param {number} params.memberLevel - 對應會員等級 level 數字
 * @param {boolean} params.enable - 是否啟用該等級折扣
 * @param {Array<Object>} [params.courseRules=[]] - 課程折扣規則
 * @param {'all'|'specific'} params.courseRules[].discountScope - 課程折扣範圍
 * @param {number|null} params.courseRules[].discountPec - 折數（整數百分比，ex: 90 = 九折）
 * @param {string[]} [params.courseRules[].limitedItemCodes=[]] - 限定課程 itemCode
 * @param {Array<Object>} [params.productRules=[]] - 商品折扣規則
 * @param {'all'|'specific'} params.productRules[].discountScope - 商品折扣範圍
 * @param {number|null} params.productRules[].discountPec - 折數（整數百分比，ex: 90 = 九折）
 * @param {number|null} [params.productRules[].minQuantity] - 商品折扣數量門檻
 * @param {string[]} [params.productRules[].limitedItemCodes=[]] - 限定商品 itemCode
 * @returns {import('@/api/instance').Api2Response<Object>} 建立後的 CyjPosLevelDiscountConfig 物件
 */
export const CreateCyjPosLevelDiscountConfig = async ({
  shopId,
  memberLevel,
  enable,
  courseRules,
  productRules,
}) => {
  return admin2({
    method: 'POST',
    url: levelDiscountBaseUrl(shopId),
    data: { memberLevel, enable, courseRules, productRules },
  })
}

/**
 * 更新 CYJ POS 等級折扣設定
 * admin.cyjPosLevelDiscountConfig.update
 * @param {Object} params - 更新參數
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 設定 ID
 * @param {boolean} [params.enable] - 是否啟用該等級折扣
 * @param {Array<Object>} [params.courseRules] - 課程折扣規則
 * @param {'all'|'specific'} params.courseRules[].discountScope - 課程折扣範圍
 * @param {number|null} params.courseRules[].discountPec - 折數（整數百分比，ex: 90 = 九折）
 * @param {string[]} [params.courseRules[].limitedItemCodes] - 限定課程 itemCode
 * @param {Array<Object>} [params.productRules] - 商品折扣規則
 * @param {'all'|'specific'} params.productRules[].discountScope - 商品折扣範圍
 * @param {number|null} params.productRules[].discountPec - 折數（整數百分比，ex: 90 = 九折）
 * @param {number|null} [params.productRules[].minQuantity] - 商品折扣數量門檻
 * @param {string[]} [params.productRules[].limitedItemCodes] - 限定商品 itemCode
 * @returns {import('@/api/instance').Api2Response<Object>} 更新後的 CyjPosLevelDiscountConfig 物件
 */
export const UpdateCyjPosLevelDiscountConfig = async ({
  shopId,
  id,
  enable,
  courseRules,
  productRules,
}) => {
  return admin2({
    method: 'PUT',
    url: `${levelDiscountBaseUrl(shopId)}/${id}`,
    data: { enable, courseRules, productRules },
  })
}

/**
 * 刪除 CYJ POS 等級折扣設定
 * admin.cyjPosLevelDiscountConfig.delete
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 設定 ID
 * @returns {import('@/api/instance').Api2Response<{ msg: string }>} 刪除結果
 */
export const DeleteCyjPosLevelDiscountConfig = async ({ shopId, id }) => {
  return admin2({
    method: 'DELETE',
    url: `${levelDiscountBaseUrl(shopId)}/${id}`,
  })
}

/**
 * 立即同步單一 CYJ POS 會員
 * admin.shopCustomModule.syncCyjPosMemberImmediately
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.memberId - 會員 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 同步後的 CyjPosMember record
 */
export const SyncCyjPosMemberImmediately = async ({ shopId, memberId }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/shopCustomModule/syncCyjPosMemberImmediately`,
    data: { memberId },
  })
}

/**
 * 查詢 CYJ POS 分店列表
 * admin.cyjPosSystem.getBranches（Admin）/ client.cyjPosSystem.getBranches（Client，相同 path）
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.branchNo] - 分店編號（選填）
 * @param {string} [params.modifiedAfter] - 修改時間下限（選填，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {import('@/api/instance').Api2Response<{ branches: Array }>} CYJ POS 分店列表
 * @example
 * // response.branches 每個元素常見欄位
 * // { branchNo, branchName, name }
 */
export const GetCyjPosBranches = async ({ shopId, branchNo, modifiedAfter }) => {
  return admin2({
    method: 'GET',
    url: branchBaseUrl(shopId),
    params: { branchNo, modifiedAfter },
  })
}

/**
 * 查詢 CYJ POS 商品列表（對應 CYJ API F-1）
 * 供後台選取商品以設定等級折扣 limitedItemCodes
 * admin.cyjPosItem.getItems
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.itemCode - 商品碼前綴（ex: '1'=一般, '2'=療程/課程），後方自動補 *
 * @param {string} [params.stateType='*'] - 啟用狀態：'*'(全部)|'0'(未啟用)|'1'(啟用)|'2'(停用)|'3'(依期限)
 * @param {string} [params.modifiedAfter] - 修改時間下限（格式：yyyy-MM-dd HH:mm:ss）
 * @returns {import('@/api/instance').Api2Response<{ items: Array }>} 商品列表
 * @example
 * // response.items 每個元素包含:
 * // { itemCode, itemName, itemSetAttr, itemPrice, saleAction, modifyTime, ... }
 * // limitedItemCodes 需使用 itemCode 值
 */
export const GetCyjPosItems = async ({ shopId, itemCode, stateType, modifiedAfter }) => {
  return admin2({
    method: 'GET',
    url: `/${shopId}/cyjPos/item`,
    params: { itemCode, stateType, modifiedAfter },
  })
}

// ─── CYJ 凱惠優惠券設定 ─────────────────────────────────────────────────────────

/**
 * 查詢 CYJ POS 票券列表
 * admin.couponCyjPos.find
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {number} [params.start=0] - 分頁起始
 * @param {number} [params.limit] - 筆數上限
 * @param {string} [params.name] - 票券名稱（模糊搜尋）
 * @param {string} [params.cyjPosEcouponId] - CYJ POS 優惠券規則 Uid
 * @returns {import('@/api/instance').Api2Response<Array>} 票券列表
 */
export const GetCyjPosCouponList = async ({ shopId, start, limit, name, cyjPosEcouponId }) => {
  return admin2({
    method: 'GET',
    url: `/${shopId}/couponCyjPos`,
    params: { start, limit, name, cyjPosEcouponId },
  })
}

/**
 * 查詢 CYJ POS 票券總數
 * admin.couponCyjPos.count
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.name] - 票券名稱（模糊搜尋）
 * @param {string} [params.cyjPosEcouponId] - CYJ POS 優惠券規則 Uid
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 票券總數
 */
export const GetCyjPosCouponCount = async ({ shopId, name, cyjPosEcouponId }) => {
  return admin2({
    method: 'GET',
    url: `/${shopId}/couponCyjPos/count`,
    params: { name, cyjPosEcouponId },
  })
}

/**
 * 查詢單一 CYJ POS 票券詳情（含 syncedCount、unsyncedCount）
 * admin.couponCyjPos.findOne
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 票券 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 票券物件，含 extraStatus.cyjPosEcouponId、extraStatus.cyjPosIsDisabled、syncedCount、unsyncedCount
 */
export const FindCyjPosCoupon = async ({ shopId, id }) => {
  return admin2({
    method: 'GET',
    url: `/${shopId}/couponCyjPos/${id}`,
  })
}

/**
 * 補發 CYJ POS 票券核銷碼
 * admin.couponCyjPos.resendCouponCode
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 票券 ID
 * @returns {import('@/api/instance').Api2Response<{ success: boolean, resendCount: number }>} 補發結果
 */
export const ResendCyjPosCouponCode = async ({ shopId, id }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/couponCyjPos/${id}/resendCouponCode`,
  })
}

/**
 * 取得 CYJ POS 優惠券發放失敗紀錄列表
 * admin.couponCyjPos.findFailedRecords
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.memberNameOrPhone] - 會員姓名或手機模糊搜尋
 * @param {string} [params.cyjPosEcouponName] - 優惠券名稱（模糊搜尋）
 * @param {string} [params.cyjPosEcouponId] - 優惠券規則 Uid
 * @param {number} [params.start=0] - 分頁起始
 * @param {number} [params.limit] - 筆數上限
 * @returns {import('@/api/instance').Api2Response<Array>} 優惠券發放失敗紀錄列表
 */
export const GetCyjPosFailedRecords = async ({
  shopId,
  memberNameOrPhone,
  cyjPosEcouponName,
  cyjPosEcouponId,
  start,
  limit,
}) => {
  return admin2({
    method: 'GET',
    url: `/${shopId}/couponCyjPos/failedRecords`,
    params: {
      memberNameOrPhone,
      cyjPosEcouponName,
      cyjPosEcouponId,
      start,
      limit,
    },
  })
}

/**
 * 取得 CYJ POS 優惠券發放失敗紀錄數量
 * admin.couponCyjPos.countFailedRecords
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.memberNameOrPhone] - 會員姓名或手機模糊搜尋
 * @param {string} [params.cyjPosEcouponName] - 優惠券名稱（模糊搜尋）
 * @param {string} [params.cyjPosEcouponId] - 優惠券規則 Uid
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 優惠券發放失敗紀錄總數
 */
export const GetCyjPosFailedRecordsCount = async ({
  shopId,
  memberNameOrPhone,
  cyjPosEcouponName,
  cyjPosEcouponId,
}) => {
  return admin2({
    method: 'GET',
    url: `/${shopId}/couponCyjPos/failedRecords/count`,
    params: {
      memberNameOrPhone,
      cyjPosEcouponName,
      cyjPosEcouponId,
    },
  })
}

/**
 * 重試所有 CYJ POS 優惠券失敗發放
 * admin.couponCyjPos.retryAllFailedIssuance
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{ success: boolean, message?: string }>} 重試結果
 */
export const RetryAllCyjPosFailedIssuance = async ({ shopId }) => {
  return admin2({
    method: 'POST',
    url: `/${shopId}/couponCyjPos/retryAllFailedIssuance`,
    data: {},
  })
}
